/**
 * Post-processing for the Ctrl+K palette's result groups.
 *
 * The autocomplete endpoint matches substrings, so "DPO" came back with
 * "Endpoint Control of …" (en-DPO-int). A result is kept when the query starts a
 * word in its visible text — after a non-alphanumeric character or at a
 * lower→upper case step, so "GroupDPO" and "ϕ-DPO" still match. A result whose
 * visible text does not contain the query at all matched on something we cannot
 * see (authors, body text); it is kept, ranked after the word matches.
 */

const isAlnum = (ch) => /[\p{L}\p{N}]/u.test(ch || '');
const isLower = (ch) => /\p{Ll}/u.test(ch || '');
const isUpper = (ch) => /\p{Lu}/u.test(ch || '');

/** 'word' | 'inside' (only mid-word) | 'absent' */
export function matchKind(text, query) {
  const q = String(query || '').trim().toLowerCase();
  const raw = String(text || '');
  if (!q) return 'absent';
  const lower = raw.toLowerCase();
  let at = lower.indexOf(q);
  if (at < 0) return 'absent';
  while (at >= 0) {
    const prev = raw[at - 1];
    if (at === 0 || !isAlnum(prev) || (isLower(prev) && isUpper(raw[at]))) return 'word';
    at = lower.indexOf(q, at + 1);
  }
  return 'inside';
}

const visibleText = (item) => [item.label, item.name, item.title, item.subtitle].filter(Boolean).join(' \u0001 ');

/** "1 connections" -> "1 connection" (the backend formats every count as plural). */
export function fixCountPlural(text) {
  if (typeof text !== 'string') return text;
  return text.replace(/(^|[^\d])1 connections\b/g, '$11 connection');
}

const dedupeKey = (item) => `${String(item.type || '').toLowerCase()}|${String(item.label || item.name || item.title || item.id || '')
  .toLowerCase().replace(/\s+/g, ' ').trim()}`;

/**
 * Filter, dedupe and rank the groups. Word matches first, then results that
 * matched on hidden fields; mid-word-only matches are dropped. Two rows with the
 * same type and the same visible name (duplicate paper records) collapse into one.
 */
export function refineGroups(groups, query) {
  const seen = new Set();
  const out = [];
  (groups || []).forEach((g) => {
    const word = [];
    const hidden = [];
    (g.items || []).forEach((item) => {
      const kind = matchKind(visibleText(item), query);
      if (kind === 'inside') return;
      const key = dedupeKey(item);
      if (seen.has(key)) return;
      seen.add(key);
      const row = item.subtitle ? { ...item, subtitle: fixCountPlural(item.subtitle) } : item;
      (kind === 'word' ? word : hidden).push(row);
    });
    const items = [...word, ...hidden];
    if (items.length) out.push({ ...g, items });
  });
  return out;
}
